import React, { useMemo, useState } from 'react';
import { PanelCommonProps } from 'config/panelConfig';
import type { AnalysisType } from 'config/analysisConfig';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';
import { labelorlineonoff } from '../Renderers/vtkManagers';
import type { ContextPool } from '../Renderers/vtkManagers';
import { initialPopup, CPRPanelPopup, ToggleKey } from './cprPanelTypes';
import { configStore } from 'config/configStore';

export function useCprAnnotationMenus({
  analysisType,
  branchIds,
  branchId,
  mainIndex,
  panelConfig,
  config,
  setData,
  contextPool,
  updateCPRState,
  labelonoff,
  isLesionStackViewLayout = false,
}: {
  analysisType: AnalysisType;
  branchIds: Array<Record<number, string>>;
  branchId: number;
  mainIndex: number;
  panelConfig: PanelCommonProps['panelConfig'];
  config: any;
  setData: (params: any) => void;
  contextPool: ContextPool;
  updateCPRState: (params: any) => void;
  labelonoff: boolean;
  isLesionStackViewLayout?: boolean;
}) {
  const [open, setOpen] = useState<CPRPanelPopup>(initialPopup);
  const [annoopen, setAnnoOpen] = useState<CPRPanelPopup>(initialPopup);

  const handlePopupOpen = (key: keyof CPRPanelPopup) => {
    setOpen({ ...initialPopup, [key]: true });
  };
  const handlePopupClose = (key: keyof CPRPanelPopup) => {
    setOpen({ ...initialPopup, [key]: false });
  };

  const handleAnnoPopupOpen = (key: keyof CPRPanelPopup) => {
    setAnnoOpen({ ...initialPopup, [key]: true });
  };
  const handleAnnoPopupClose = (key: keyof CPRPanelPopup) => {
    setAnnoOpen({ ...initialPopup, [key]: false });
  };

  const projectionContextMenuItems: SimpleContextMenuItem[] = useMemo(
    () =>
      (branchIds ?? [])
        .filter((item) => {
          const entry = Object.entries(item)[0];
          const label = entry?.[1];
          return !!label;
        })
        .map((item) => {
          const [id, label] = Object.entries(item)[0];
          return {
            label,
            onClick: () => {
              updateCPRState({
                mainIndex: { index: mainIndex, branchId: Number(id) },
              });
              handlePopupClose('projection');
            },
            isChecked: Number(id) === branchId,
          };
        }),
    [branchIds, branchId, mainIndex, open['projection']],
  );

  const bottomLeftContent = useMemo(() => {
    if (!branchIds || branchIds.length === 0) return;
    const result = branchIds
      .slice(0)
      .find((obj) => Object.prototype.hasOwnProperty.call(obj, branchId));
    if (!result) return;

    let name = '(UNCERTAIN)';
    if (analysisType === 'aorticValveCalcium' || analysisType === 'tavi') {
      name = 'CPR';
    } else {
      const [, label] = Object.entries(result)[0];
      name = label;
    }

    return (
      <div
        className={`projection-text ${panelConfig.componentKey}`}
        onClick={() => {
          if (
            analysisType === 'plaque' ||
            analysisType === 'isr' ||
            analysisType === 'stenosis'
          ) {
            handlePopupOpen('projection');
          }
        }}
      >
        {name || '(UNCERTAIN)'}
      </div>
    );
  }, [branchIds, branchId, analysisType, panelConfig.componentKey]);

  const bottomRightContent = useMemo(() => {
    if (analysisType === 'tavi') return;
    return (
      <div
        className={`projection-text ${panelConfig.componentKey}`}
        onClick={() => {
          handleAnnoPopupOpen('projection');
        }}
      >
        Overlays
      </div>
    );
  }, [branchIds, branchId, analysisType, panelConfig.componentKey]);

  const annotationMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const label = config.viewOverlayLabels?.cpr ?? config.label;

    const items: Array<{ key: ToggleKey; text: string; show: boolean }> =
      analysisType === 'tavi'
        ? []
        : analysisType === 'aorticValveCalcium'
          ? [
              { key: 'all', text: 'All', show: true },
              { key: 'markerline', text: 'Line', show: configStore.get().EditMode !== 'lumen' },
              { key: 'aorta', text: 'Aorta', show: true },
            ]
          : isLesionStackViewLayout
            ? [
                { key: 'guideline', text: 'VesselBorder', show: configStore.get().EditMode !== 'lumen' },
                { key: 'markerline', text: 'Lesion', show: true },
              ]
            : [
              { key: 'all', text: 'All', show: true },
              {
                key: 'isrline',
                text: 'ISRLesion',
                show: analysisType === 'isr',
              },
              {
                key: 'stenosisline',
                text: 'StenosisLesion',
                show: analysisType === 'stenosis',
              },
              { key: 'guideline', text: 'VesselBorder', show: configStore.get().EditMode !== 'lumen' },
              { key: 'markerline', text: 'Lesion', show: analysisType !== 'FFR' },
              {
                key: 'plaqueoutline',
                text: 'PlaqueOutline',
                show: analysisType === 'plaque',
              },
              { key: 'fai', text: 'FAI', show: analysisType === 'fai' },
              {
                key: 'plaque',
                text: 'Plaque',
                show: analysisType === 'plaque',
              },
              { key: 'lumen', text: 'Lumen', show: true },
              {
                key: 'lesionline',
                text: 'PlaqueLesion',
                show: analysisType === 'plaque',
              },
            ];

    const visibleItems = items.filter((item) => item.show);
    const visibleToggleKeys = visibleItems
      .map((item) => item.key)
      .filter((key): key is Exclude<ToggleKey, 'all'> => key !== 'all');
    const allTrue =
      visibleToggleKeys.length > 0 &&
      visibleToggleKeys.every((key) => label[key] !== false);

    const updateCprOverlayLabel = (
      nextLabel: typeof label,
      changedKeys: Exclude<ToggleKey, 'all'>[],
      forcedValue?: boolean,
    ) => {
      setData((prev: any) => ({
        viewOverlayLabels: {
          ...prev.viewOverlayLabels,
          cpr: {
            ...nextLabel,
            lumenoutline: false,
          },
        },
      }));
      changedKeys.forEach((changedKey) => {
        labelorlineonoff(
          analysisType,
          contextPool,
          changedKey,
          true,
          forcedValue ?? nextLabel[changedKey],
          'cpr',
        );
      });
    };

    const toggle = (key: ToggleKey) => {
      if (key === 'all') {
        const nextValue = !allTrue;
        const nextLabel = {
          ...label,
          lumenoutline: false,
        } as typeof label;
        visibleToggleKeys.forEach((visibleKey) => {
          nextLabel[visibleKey] = nextValue;
        });
        updateCprOverlayLabel(nextLabel, visibleToggleKeys, nextValue);
      } else {
        const nextValue = !label[key];
        const nextLabel = {
          ...label,
          [key]: nextValue,
          lumenoutline: false,
        } as typeof label;
        updateCprOverlayLabel(nextLabel, [key], nextValue);
      }
    };

    return visibleItems.map(({ key, text }) => {
      return {
        label: text,
        onClick: () => {
          toggle(key);
        },
        isChecked: key === 'all' ? allTrue : label[key] !== false,
      };
    });
  }, [
    analysisType,
    isLesionStackViewLayout,
    config.label,
    config.viewOverlayLabels,
    contextPool,
    labelonoff,
    annoopen['projection'],
  ]);

  return {
    open,
    annoopen,
    handlePopupClose,
    handleAnnoPopupClose,
    projectionContextMenuItems,
    bottomLeftContent,
    bottomRightContent,
    annotationMenuItems,
  };
}

export { SimpleContextMenu };
